import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { authMe, authLogin, authRegister, authLogout, setStoredToken, getStoredToken } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // null = checking, false = logged out, object = logged in
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        // Skip /me check while we're on the OAuth callback — it will set the token first
        if (window.location.hash?.includes("session_id=")) {
            setUser(false);
            return;
        }
        if (!getStoredToken()) {
            setUser(false);
            return;
        }
        try {
            const me = await authMe();
            setUser(me);
        } catch (_e) {
            setUser(false);
        }
    }, []);

    useEffect(() => { checkAuth(); }, [checkAuth]);

    const login = useCallback(async (email, password, opts = {}) => {
        setError(null);
        const data = await authLogin({ email, password, remember_me: !!opts.rememberMe });
        setStoredToken(data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (email, password, name, opts = {}) => {
        setError(null);
        const data = await authRegister({ email, password, name, remember_me: !!opts.rememberMe });
        setStoredToken(data.access_token);
        setUser(data.user);
        return { user: data.user, migrated: data.migrated };
    }, []);

    const logout = useCallback(async () => {
        try {
            await authLogout();
        } catch (err) {
            // Server-side logout failure is non-fatal — we still clear local token.
            if (process.env.NODE_ENV !== "production") {
                console.warn("[auth] logout request failed (clearing local session anyway):", err?.message);
            }
        }
        setStoredToken(null);
        setUser(false);
    }, []);

    const loginWithToken = useCallback(async (token, userObj) => {
        setStoredToken(token);
        setUser(userObj);
    }, []);

    const value = useMemo(() => ({
        user,
        error,
        setError,
        login,
        register,
        logout,
        loginWithToken,
        refresh: checkAuth,
    }), [user, error, login, register, logout, loginWithToken, checkAuth]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
